import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ClipboardCheck } from "lucide-react";

export default function Apply() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [cover, setCover] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(()=>{ api.get(`/jobs/${jobId}`).then(r=>setJob(r.data)); },[jobId]);
  if (!job) return <div><PublicHeader/><div className="p-10 text-slate-500">Loading...</div></div>;

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/applications", { job_id: jobId, cover_note: cover });
      if (data.duplicate) {
        toast.info("You already applied for this role.");
        if (data.assessment_id) nav(`/assessment/${data.assessment_id}`);
        else nav("/candidate/dashboard");
        return;
      }
      toast.success("Application started — complete the assessment to submit.");
      if (data.assessment_id) nav(`/assessment/${data.assessment_id}`);
      else nav("/candidate/dashboard");
    } catch(e) {
      toast.error(e.response?.data?.detail || "Failed to apply");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader/>
      <div className="max-w-3xl mx-auto p-6">
        <div className="text-xs uppercase tracking-wider text-[#2563EB] font-semibold">Apply</div>
        <h1 className="font-display text-3xl font-bold text-[#0A2540] mt-1">{job.title}</h1>
        <p className="text-slate-500 mt-1">{job.department} · {job.location} · {job.employment_type}</p>

        <Card className="p-6 mt-8 border-slate-200">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-amber-100"><ClipboardCheck className="w-5 h-5 text-amber-700"/></div>
            <div>
              <div className="font-semibold text-[#0A2540]">Skill Assessment Required</div>
              <p className="text-sm text-slate-600 mt-1">
                On submit, we'll automatically generate a job-specific assessment based on the required skills for this role
                ({job.required_skills?.join(", ")}). Your application will be finalized once you complete the assessment.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6 mt-6 border-slate-200">
          <label className="text-sm font-semibold text-[#0A2540]">Cover note (optional)</label>
          <Textarea rows={5} placeholder="Share why you're a strong fit for this role..." value={cover} onChange={e=>setCover(e.target.value)} className="mt-2" data-testid="apply-cover"/>
          <div className="mt-4 flex flex-wrap gap-2">
            {job.required_skills?.map(s=><Badge key={s} className="bg-blue-100 text-blue-800 border-none hover:bg-blue-100">{s}</Badge>)}
          </div>
        </Card>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="ghost" onClick={()=>nav(-1)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="apply-submit">
            {loading ? "Submitting..." : "Submit & Start Assessment"} <ArrowRight className="w-4 h-4 ml-2"/>
          </Button>
        </div>
      </div>
    </div>
  );
}
